<template>
  <div
    class="trezo-card bg-white dark:bg-[#0c1427] mb-[25px] p-[20px] md:p-[25px] rounded-md"
  >
    <div
      class="trezo-card-header mb-[20px] md:mb-[25px] flex items-center justify-between"
    >
      <div class="trezo-card-title">
        <h5 class="mb-0">{{ directT('dashboard.crm.balanceOverview') }}</h5>
      </div>
      <div class="trezo-card-subtitle">
        <div class="trezo-card-dropdown relative">
          <button
            type="button"
            :class="{
              'trezo-card-dropdown-btn inline-block rounded-md border border-gray-100 py-[5px] md:py-[6.5px] px-[12px] md:px-[19px] transition-all hover:bg-gray-50 dark:border-[#172036] dark:hover:bg-[#0a0e19]': true,
              active: isActive,
            }"
            id="dropdownToggle"
            @click.stop="toggleActive"
          >
            <span
              class="inline-block relative ltr:pr-[17px] ltr:md:pr-[20px] rtl:pl-[17px] rtl:ml:pr-[20px]"
            >
              {{ getPeriodLabel() }}
              <i
                class="ri-arrow-down-s-line text-lg absolute ltr:-right-[3px] rtl:-left-[3px] top-1/2 -translate-y-1/2"
              ></i>
            </span>
          </button>
          <ul
            v-if="isActive"
            class="trezo-card-dropdown-menu transition-all bg-white shadow-3xl rounded-md top-full py-[15px] absolute ltr:right-0 rtl:left-0 w-[195px] z-[5] dark:bg-dark dark:shadow-none"
          >
            <li>
              <button
                type="button"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
                @click="setPeriod('week')"
              >
                {{ directT('dashboard.thisWeek') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
                @click="setPeriod('month')"
              >
                {{ directT('dashboard.thisMonth') }}
              </button>
            </li>
            <li>
              <button
                type="button"
                class="block w-full transition-all text-black ltr:text-left rtl:text-right relative py-[8px] px-[20px] hover:bg-gray-50 dark:text-white dark:hover:bg-black"
                @click="setPeriod('year')"
              >
                {{ directT('dashboard.thisYear') }}
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="trezo-card-content">
      <div v-if="loading" class="flex justify-center items-center h-[350px]">
        <span class="animate-pulse">{{ directT('general.loading') }}...</span>
      </div>
      <div v-else class="-mb-[3px] ltr:-ml-[10px] rtl:-mr-[10px]">
        <apexchart
          v-if="isClient"
          type="area"
          height="350"
          :options="balanceOverview"
          :series="overview"
        ></apexchart>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed, watch } from "vue";
import { useClickOutside } from "@/utils/useClickOutside";
import { useI18n } from "vue-i18n";
import { supabase } from "@/lib/supabaseClient";

// Direct import of translation files for manual translation lookup
import enTranslations from '@/i18n/locales/en.json';
import heTranslations from '@/i18n/locales/he.json';
import arTranslations from '@/i18n/locales/ar.json';

// Create type for translation object structure
type TranslationObject = Record<string, any>;

interface Deal {
  id: string;
  amount: number | string;
  created_at: string;
  [key: string]: any;
}

interface Organization {
  id: string;
  currency: string;
  [key: string]: any;
}

// Types for time periods
type TimePeriod = 'week' | 'month' | 'year';

export default defineComponent({
  name: "BalanceOverviewChart",
  setup() {
    const { locale } = useI18n();
    const { isActive, toggleActive } = useClickOutside("dropdownToggle");
    const isClient = ref(false);
    const loading = ref(true);
    const deals = ref<Deal[]>([]);
    const currencySymbol = ref('$');
    const selectedPeriod = ref<TimePeriod>('year');
    const totalRevenue = ref(0);
    const totalExpenses = ref(0);
    const periodDataRevenue = ref<number[]>([]);
    const periodDataExpenses = ref<number[]>([]);
    
    // Store direct references to translation files
    const translationFiles = {
      en: enTranslations,
      he: heTranslations,
      ar: arTranslations
    };

    // Direct translation function that bypasses vue-i18n
    const directT = (key: string): string => {
      const parts = key.split('.');
      let result: TranslationObject = translationFiles[locale.value as keyof typeof translationFiles];
      
      for (const part of parts) {
        if (!result || !(part in result)) {
          console.warn(`Missing translation key: ${key} in ${locale.value} locale`);
          // Fallback to English if translation not found
          let enResult: TranslationObject = translationFiles.en;
          for (const fallbackPart of parts) {
            if (!enResult || !(fallbackPart in enResult)) return key;
            enResult = enResult[fallbackPart as keyof typeof enResult] as TranslationObject;
          }
          return typeof enResult === 'string' ? enResult : key;
        }
        result = result[part as keyof typeof result] as TranslationObject;
      }
      return typeof result === 'string' ? result : key;
    };
    
    // Computed properties
    const profitRatio = computed(() => {
      if (totalRevenue.value === 0) return 0;
      return (totalRevenue.value - totalExpenses.value) / totalRevenue.value * 100;
    });
    
    const overview = computed(() => {
      return [
        {
          name: directT('dashboard.revenue'),
          data: periodDataRevenue.value,
        },
      ];
    });
    
    // Computed for x-axis categories based on selected period
    const getCategories = computed(() => {
      const today = new Date();
      
      if (selectedPeriod.value === 'week') {
        // Return days of the week
        const days = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          days.push(date.toLocaleString(locale.value, { weekday: 'short' }));
        }
        return days;
      } else if (selectedPeriod.value === 'month') {
        // Return days of the month (current month)
        const days = [];
        const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
        for (let i = 1; i <= daysInMonth; i++) {
          days.push(i.toString());
        }
        return days;
      } else {
        // Return months of the year
        const months = [];
        for (let i = 0; i < 12; i++) {
          const date = new Date(today.getFullYear(), i, 1);
          months.push(date.toLocaleString(locale.value, { month: 'short' }));
        }
        return months;
      }
    });
    
    // Balance chart options
    const balanceOverview = computed(() => {
      return {
        chart: {
          type: "area",
          height: 350,
          zoom: {
            enabled: false,
          },
        },
        colors: ["#AD63F6"],
        dataLabels: {
          enabled: false,
        },
        grid: {
          borderColor: "#ECEEF2",
        },
        stroke: {
          curve: "straight",
          width: 2,
        },
        xaxis: {
          categories: getCategories.value,
          axisTicks: {
            show: false,
            color: "#B1BBC8",
          },
          axisBorder: {
            show: false,
            color: "#B1BBC8",
          },
          labels: {
            show: true,
            style: {
              colors: "#8695AA",
              fontSize: "12px",
            },
          },
        },
        yaxis: {
          tickAmount: 6,
          labels: {
            formatter: (val: string) => {
              return currencySymbol.value + val;
            },
            style: {
              colors: "#64748B",
              fontSize: "12px",
            },
          },
        },
        legend: {
          show: true,
          position: "top",
          fontSize: "12px",
          horizontalAlign: "left",
          itemMargin: {
            horizontal: 8,
            vertical: 0,
          },
          labels: {
            colors: "#64748B",
          },
          markers: {
            width: 9,
            height: 9,
            offsetX: -2,
            offsetY: -0.5,
            radius: 2,
            shape: "diamond",
          },
        },
        tooltip: {
          y: {
            formatter: (val: number) => {
              return currencySymbol.value + val.toLocaleString();
            }
          }
        }
      };
    });

    // Get label for current period
    const getPeriodLabel = () => {
      if (selectedPeriod.value === 'week') {
        return directT('dashboard.thisWeek');
      } else if (selectedPeriod.value === 'month') {
        return directT('dashboard.thisMonth');
      } else {
        return directT('dashboard.thisYear');
      }
    };

    // Set period and fetch data
    const setPeriod = (period: TimePeriod) => {
      selectedPeriod.value = period;
      fetchDealsData();
      toggleActive();
    };

    // Get date range based on selected period
    const getDateRange = () => {
      const today = new Date();
      let startDate = new Date();
      
      if (selectedPeriod.value === 'week') {
        // Last 7 days
        startDate.setDate(today.getDate() - 6);
      } else if (selectedPeriod.value === 'month') {
        // Current month
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      } else {
        // Current year
        startDate = new Date(today.getFullYear(), 0, 1);
      }
      
      return { startDate, endDate: today };
    };

    // Fetch deals data from Supabase
    const fetchDealsData = async () => {
      try {
        loading.value = true;
        
        // Get current user's organization
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          console.warn('User not authenticated');
          loading.value = false;
          return;
        }
        
        // Get the user's organization ID
        const { data: userProfile, error: profileError } = await supabase
          .from('user_profiles')
          .select('org_id')
          .eq('user_id', user.id)
          .single();
        
        if (profileError || !userProfile?.org_id) {
          console.warn('User has no organization');
          loading.value = false;
          return;
        }
        
        const organizationId = userProfile.org_id as string;
        
        // Get organization's currency
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('currency')
          .eq('id', organizationId)
          .single();
          
        if (orgError) {
          console.error('Error fetching organization data:', orgError);
        } else if (orgData && orgData.currency) {
          currencySymbol.value = (orgData as Organization).currency;
        }
        
        // Get date range for fetching deals
        const { startDate, endDate } = getDateRange();
        
        // Fetch deals for the selected period
        const { data, error } = await supabase
          .from('deals')
          .select('id, amount, created_at, status')
          .eq('organization_id', organizationId)
          .gte('created_at', startDate.toISOString())
          .lte('created_at', endDate.toISOString())
          .order('created_at', { ascending: true });
        
        if (error) {
          console.error('Error fetching deals:', error);
          loading.value = false;
          return;
        }
        
        deals.value = data as Deal[];
        
        // Process data based on the selected period
        processData();
        
      } catch (error) {
        console.error('Error in fetchDealsData:', error);
      } finally {
        loading.value = false;
      }
    };

    // Process the data based on selected period
    const processData = () => {
      // Reset totals
      totalRevenue.value = 0;
      totalExpenses.value = 0;
      
      const today = new Date();
      
      if (selectedPeriod.value === 'week') {
        // Process data for the week (last 7 days)
        const dailyRevenue = Array(7).fill(0);
        const dailyExpenses = Array(7).fill(0);
        
        deals.value.forEach(deal => {
          const dealAmount = typeof deal.amount === 'string' ? parseFloat(deal.amount) : deal.amount;
          if (isNaN(dealAmount)) return;
          
          const dealDate = new Date(deal.created_at);
          const diffDays = Math.floor((today.getTime() - dealDate.getTime()) / (1000 * 60 * 60 * 24));
          
          if (diffDays >= 0 && diffDays < 7) {
            totalRevenue.value += dealAmount;
            dailyRevenue[6 - diffDays] += dealAmount;
            
            // Still calculate expenses for internal use but don't display
            const expense = dealAmount * 0.6;
            totalExpenses.value += expense;
            dailyExpenses[6 - diffDays] += expense;
          }
        });
        
        periodDataRevenue.value = dailyRevenue;
        periodDataExpenses.value = dailyExpenses;
        
      } else if (selectedPeriod.value === 'month') {
        // Process data for the month (current month)
        const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
        const dailyRevenue = Array(daysInMonth).fill(0);
        const dailyExpenses = Array(daysInMonth).fill(0);
        
        deals.value.forEach(deal => {
          const dealAmount = typeof deal.amount === 'string' ? parseFloat(deal.amount) : deal.amount;
          if (isNaN(dealAmount)) return;
          
          const dealDate = new Date(deal.created_at);
          
          // Check if deal is from current month
          if (dealDate.getMonth() === today.getMonth() && dealDate.getFullYear() === today.getFullYear()) {
            const dayOfMonth = dealDate.getDate() - 1; // 0-based index
            
            totalRevenue.value += dealAmount;
            dailyRevenue[dayOfMonth] += dealAmount;
            
            // Still calculate expenses for internal use but don't display
            const expense = dealAmount * 0.6;
            totalExpenses.value += expense;
            dailyExpenses[dayOfMonth] += expense;
          }
        });
        
        periodDataRevenue.value = dailyRevenue;
        periodDataExpenses.value = dailyExpenses;
        
      } else {
        // Process data for the year (current year)
        const monthlyRevenue = Array(12).fill(0);
        const monthlyExpenses = Array(12).fill(0);
        
        deals.value.forEach(deal => {
          const dealAmount = typeof deal.amount === 'string' ? parseFloat(deal.amount) : deal.amount;
          if (isNaN(dealAmount)) return;
          
          const dealDate = new Date(deal.created_at);
          
          // Check if deal is from current year
          if (dealDate.getFullYear() === today.getFullYear()) {
            const monthIndex = dealDate.getMonth();
            
            totalRevenue.value += dealAmount;
            monthlyRevenue[monthIndex] += dealAmount;
            
            // Still calculate expenses for internal use but don't display
            const expense = dealAmount * 0.6;
            totalExpenses.value += expense;
            monthlyExpenses[monthIndex] += expense;
          }
        });
        
        periodDataRevenue.value = monthlyRevenue;
        periodDataExpenses.value = monthlyExpenses;
      }
    };

    onMounted(() => {
      isClient.value = true;
      fetchDealsData();
    });

    // Watch for locale changes and refresh data
    watch(locale, () => {
      fetchDealsData();
    });

    return {
      isClient,
      loading,
      overview,
      balanceOverview,
      isActive,
      toggleActive,
      totalRevenue,
      totalExpenses,
      profitRatio,
      currencySymbol,
      selectedPeriod,
      setPeriod,
      getPeriodLabel,
      directT,
    };
  },
});
</script>
